import { Divider, ListSubheader } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { FlexibleSelectTextField } from '@/components/atoms/FlexibleSelectTextField';
import { BalanceAccountDetailsItem } from '@/components/molecules/BalanceAccountDetailsItem';
import { CryptoAccountDetailsItem } from '@/components/molecules/CryptoAccountDetailsItem';
import { FundingMethodMenuItem } from '@/components/molecules/FundingMethodMenuItem';
import { useTranslator } from '@/hooks/useTranslator';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType, FundingMethodItem, PaymentRailType } from '@/types/currency';
import { isZeroOrLess } from '@/utility/isZero';

interface Props {
  readonly label: string;
  readonly items: readonly FundingMethodItem[];
  readonly value: string;
  readonly balances: Partial<Record<CurrencyCodeType, DetailedBalance>>;

  readonly error?: boolean;
  readonly name?: string;
  readonly helperText?: string | null;

  readonly customerBalances?: Partial<Record<CurrencyCodeType, DetailedBalance>>;
  readonly showCustomerSection?: boolean;
  readonly customerCurrencies?: readonly CurrencyCodeType[];

  onChange(value: FundingMethodItem): void;
}

export const FundingMethodSelect: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    label,
    items,
    value,
    name,
    balances,
    onChange,
    error,
    helperText,
    customerBalances,
    showCustomerSection = false,
    customerCurrencies,
  } = props;

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: methodId } = event.target;

      // Check customer balance items first (id format: "customer.CURRENCY")
      if (showCustomerSection && methodId.startsWith('customer.')) {
        const currencyStr = methodId.replace('customer.', '');
        const currency = currencyStr as CurrencyCodeType;
        onChange({
          id: methodId,
          method: PaymentRailType.CalizaWallet,
          currency: currency,
          fundingSource: 'customer',
        });
        return;
      }

      const found = items.find((item: FundingMethodItem): boolean => item.id === methodId);
      if (!found) {
        console.debug(`Funding method with id ${methodId} not found`);
        return;
      }

      onChange({
        ...found,
        fundingSource: found.method === PaymentRailType.CalizaWallet ? 'integrator' : undefined,
      });
    },
    [items, onChange, showCustomerSection],
  );

  const wallets = React.useMemo((): readonly FundingMethodItem[] => {
    return items.filter((item: FundingMethodItem): boolean => {
      return item.method === PaymentRailType.CalizaWallet;
    });
  }, [items]);

  const methods = React.useMemo((): readonly FundingMethodItem[] => {
    return items.filter(
      (item: FundingMethodItem): boolean => item.method !== PaymentRailType.CalizaWallet,
    );
  }, [items]);

  // Derive available wallet currencies from the wallets (CalizaWallet items)
  const availableWalletCurrencies = React.useMemo((): readonly CurrencyCodeType[] => {
    const currencies = wallets.map((item: FundingMethodItem): CurrencyCodeType => item.currency);
    // Remove duplicates
    return [...new Set(currencies)];
  }, [wallets]);

  const customerWalletItems = React.useMemo((): readonly CurrencyCodeType[] => {
    if (!showCustomerSection || !customerBalances) {
      return [];
    }

    // Use customerCurrencies if provided, otherwise fall back to availableWalletCurrencies
    const currenciesToShow = customerCurrencies ?? availableWalletCurrencies;

    return currenciesToShow;
  }, [showCustomerSection, customerBalances, customerCurrencies, availableWalletCurrencies]);

  const tr = useTranslator();

  const hasCustomerSection = showCustomerSection && customerWalletItems.length > 0;
  const hasIntegratorSection = wallets.length > 0;
  const hasExternalMethods = methods.length > 0;

  return (
    <FlexibleSelectTextField
      name={name}
      label={label}
      select={true}
      fullWidth={true}
      value={value}
      error={error}
      helperText={helperText}
      onChange={handleChange}>
      {hasCustomerSection && <ListSubheader>{tr('CUSTOMER_BALANCE')}</ListSubheader>}
      {hasCustomerSection &&
        customerWalletItems.map((currency: CurrencyCodeType): React.ReactElement => {
          const customerId = `customer.${currency}`;
          const balance = customerBalances?.[currency] ?? DetailedBalance.zero(currency);
          const outOfFunds = isZeroOrLess(balance.available);

          return (
            <MenuItem key={customerId} value={customerId} disabled={outOfFunds}>
              <BalanceAccountDetailsItem
                balance={balance}
                currency={currency}
                available={!outOfFunds}
              />
            </MenuItem>
          );
        })}

      {hasCustomerSection && hasIntegratorSection && <Divider />}
      {hasCustomerSection && hasIntegratorSection && <ListSubheader sx={styles.spacer} />}

      {hasIntegratorSection && (
        <ListSubheader>
          {hasCustomerSection ? tr('INTEGRATOR_BALANCE') : tr('USE_YOUR_CALIZA_BALANCE')}
        </ListSubheader>
      )}
      {wallets.map((fundingMethod: FundingMethodItem): React.ReactElement => {
        const { currency } = fundingMethod;

        const balance = balances[currency] ?? DetailedBalance.zero(currency);
        const outOfFunds = isZeroOrLess(balance.available);

        return (
          <MenuItem key={fundingMethod.id} value={fundingMethod.id} disabled={outOfFunds}>
            <CryptoAccountDetailsItem
              balanceAmount={balance.available}
              currency={currency}
              available={!outOfFunds}
            />
          </MenuItem>
        );
      })}

      {hasExternalMethods && (hasIntegratorSection || hasCustomerSection) && <Divider />}
      {hasExternalMethods && <ListSubheader sx={styles.spacer} />}
      {hasExternalMethods && <ListSubheader>{tr('SEND_FUNDS_TO_COMPLETE_TRANSFER')}</ListSubheader>}
      {methods.map((fundingMethod: FundingMethodItem): React.ReactElement => {
        return (
          <MenuItem key={fundingMethod.id} value={fundingMethod.id}>
            <FundingMethodMenuItem fundingMethod={fundingMethod} />
          </MenuItem>
        );
      })}
    </FlexibleSelectTextField>
  );
};

const styles: Record<string, SystemStyleObject> = {
  spacer: {
    pt: 0,
  },
};
